import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const SceneCustomProject = ({ onOpenForm }) => {
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  return (
    <section 
      ref={sectionRef}
      className="scene relative min-h-[70vh] py-24 overflow-hidden"
      id="custom-project"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-frmla-charcoal to-background" />
      
      <motion.div 
        className="relative z-10 w-full max-w-4xl mx-auto px-6"
        style={{ opacity }}
      >
        <div className="text-center">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <p className="font-body text-sm tracking-[0.3em] text-accent/80 uppercase mb-4">
              CUSTOM WORK
            </p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
              HAVE SOMETHING SPECIFIC?
            </h2>
          </motion.div>
          
          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto mb-12"
          >
            <p className="font-body text-lg text-muted-foreground leading-relaxed mb-6">
              Not every project fits neatly into a package — and that's intentional.
            </p>
            <p className="font-body text-base text-muted-foreground/80 leading-relaxed mb-6">
              This option is for brands and artists who know what they need, need multiple pieces, or are working outside the scope of our standard offerings.
            </p>
            <p className="font-body text-base text-muted-foreground/80 leading-relaxed">
              If that sounds like you, tell us what you're trying to build and we'll respond with a clear game plan — or suggest a better option if something isn't the right fit.
            </p>
          </motion.div>
          
          {/* Gold divider */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
            className="gold-line w-48 mx-auto mb-12"
          />
          
          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
            viewport={{ once: true }}
          >
            <button
              onClick={onOpenForm}
              data-testid="custom-project-cta"
              className="inline-flex items-center gap-3 px-10 py-5 font-display text-lg tracking-wider bg-foreground text-background hover:bg-accent transition-colors duration-400"
              style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
            >
              GET A GAME PLAN
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default SceneCustomProject;
